import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { CheckCircle, XCircle, Loader2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Play, Pause, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const AUTO_REFRESH_INTERVAL = 60; // seconds

export default function DailyReconciliationPanel({ defaultDate, autoLoad = false }) {
  const { getAuthHeaders } = useAuth();
  const [date, setDate] = useState(defaultDate || todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/daily-import/reconciliation?date=${d}`, {
        headers: getAuthHeaders(),
      });
      setData(res);
      setLastRefreshed(new Date());
      setCountdown(AUTO_REFRESH_INTERVAL);
    } catch (err) {
      toast.error('Failed to load reconciliation');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { if (autoLoad) load(date); }, [autoLoad, load, date]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh && data) {
      intervalRef.current = setInterval(() => load(date), AUTO_REFRESH_INTERVAL * 1000);
      countdownRef.current = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    }
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [autoRefresh, date, data, load]);

  // Reset countdown when loading completes
  useEffect(() => {
    if (!loading && autoRefresh) setCountdown(AUTO_REFRESH_INTERVAL);
  }, [loading, autoRefresh]);

  const toggleAutoRefresh = () => {
    if (!data) { load(date); }
    setAutoRefresh(prev => !prev);
    setCountdown(AUTO_REFRESH_INTERVAL);
  };

  return (
    <div className="space-y-4" data-testid="reconciliation-panel">
      {/* Date picker + load + auto-refresh */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          type="date"
          value={date}
          onChange={e => { setDate(e.target.value); setAutoRefresh(false); }}
          className="w-44"
          data-testid="recon-date-input"
        />
        <Button onClick={() => load(date)} disabled={loading} variant="outline" size="sm" data-testid="recon-load-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          {loading ? 'Loading...' : 'Load'}
        </Button>
        <Button
          onClick={toggleAutoRefresh}
          variant={autoRefresh ? 'default' : 'outline'}
          size="sm"
          className={autoRefresh ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
          data-testid="recon-auto-refresh-toggle"
        >
          {autoRefresh ? <Pause className="h-3.5 w-3.5 mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
          {autoRefresh ? 'Pause' : 'Live'}
        </Button>
        {autoRefresh && (
          <span className="text-xs text-green-600 flex items-center gap-1" data-testid="recon-auto-refresh-countdown">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            Next refresh in {countdown}s
          </span>
        )}
        {lastRefreshed && !autoRefresh && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Last: {lastRefreshed.toLocaleTimeString()}
          </span>
        )}
        {data && <span className="text-sm text-slate-500">Showing activity for {data.date}</span>}
      </div>

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: TrendingUp, label: 'Payments', amount: data.total_payments_amount, deals: data.total_payments_deals, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
              { icon: TrendingDown, label: 'Bounces', amount: data.total_bounces_amount, deals: data.total_bounces_deals, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
              { icon: DollarSign, label: 'Bounce Fees', amount: data.total_fees_amount, deals: data.total_bounces_deals, color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
              { icon: CheckCircle, label: 'Net Collected', amount: data.total_payments_amount - data.total_bounces_amount, deals: null, color: data.total_payments_amount - data.total_bounces_amount >= 0 ? 'text-blue-700' : 'text-red-700', bg: 'bg-blue-50', border: 'border-blue-200' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} ${s.border} border rounded-lg p-3`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <s.icon className={`h-4 w-4 ${s.color}`} />
                  <span className="text-xs font-medium text-slate-600">{s.label}</span>
                </div>
                <div className={`text-xl font-bold ${s.color}`}>{fmt(s.amount)}</div>
                {s.deals !== null && <div className="text-xs text-slate-500 mt-0.5">{s.deals} deal{s.deals !== 1 ? 's' : ''}</div>}
              </div>
            ))}
          </div>

          {/* Payments table */}
          {data.payments?.length > 0 && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  Deals Receiving Payments ({data.payments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 pl-4">Deal ID</TableHead>
                        <TableHead className="text-xs py-2">Merchant</TableHead>
                        <TableHead className="text-xs py-2">Amount</TableHead>
                        <TableHead className="text-xs py-2">Count</TableHead>
                        <TableHead className="text-xs py-2">New Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.payments.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 text-xs font-mono pl-4">{p.deal_id}</TableCell>
                          <TableCell className="py-2 text-xs">{p.merchant_name || p.deal_name}</TableCell>
                          <TableCell className="py-2 text-xs text-green-700 font-medium">{fmt(p.total)}</TableCell>
                          <TableCell className="py-2 text-xs text-slate-500">{p.txn_count}</TableCell>
                          <TableCell className="py-2 text-xs text-slate-700">{fmt(p.new_balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Bounces table */}
          {data.bounces?.length > 0 && (
            <Card>
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                  Deals with Bounces ({data.bounces.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs py-2 pl-4">Deal ID</TableHead>
                        <TableHead className="text-xs py-2">Merchant</TableHead>
                        <TableHead className="text-xs py-2">Amount</TableHead>
                        <TableHead className="text-xs py-2">Return Codes</TableHead>
                        <TableHead className="text-xs py-2">New Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.bounces.map((b, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-2 text-xs font-mono pl-4">{b.deal_id}</TableCell>
                          <TableCell className="py-2 text-xs">{b.merchant_name || b.deal_name}</TableCell>
                          <TableCell className="py-2 text-xs text-red-700 font-medium">-{fmt(b.total)}</TableCell>
                          <TableCell className="py-2 text-xs">
                            {b.return_codes.map(rc => (
                              <span key={rc} className="inline-block mr-1 font-mono bg-red-50 text-red-700 px-1 rounded text-[10px]">{rc}</span>
                            ))}
                          </TableCell>
                          <TableCell className="py-2 text-xs text-slate-700">{fmt(b.new_balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {data.payments?.length === 0 && data.bounces?.length === 0 && (
            <div className="text-center py-10 text-slate-400">
              <XCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No payment or bounce activity found for {data.date}</p>
            </div>
          )}
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-8 text-slate-400">
          <RefreshCw className="h-6 w-6 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Select a date and click Load to see activity</p>
        </div>
      )}
    </div>
  );
}
